import React, { useEffect, useState } from 'react';
import { ActionType } from '@common/enums/command-hub/command-buttons';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CustomSplitColumnCard from '@common/components/misc/CustomSplitColumnCard';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  CloudTokenIcon,
  CopyIcon,
  DownloadIcon,
  HideInputIcon,
  PasswordIcon,
  RefreshIcon,
  ServiceConnectionIcon,
  ShowInputIcon,
  SyncErrorIcon,
  SyncIcon,
} from '@common/utils/icons/icons';
import { selectRobotsData } from '../../../store/robots/robots-slice-selectors';
import CustomLabelTextTable from '../../misc/CustomLabelTextTable';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import CircularProgress from '@mui/material/CircularProgress';
import SyncStatus from '../../../models/enums/sync-status';
import { dateToStringFormat } from '@common/utils/date';
import AuthenticationSettings from '../AuthenticationSettings';
import { selectUser } from '../../../store/general/general-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { isHttpError } from '@common/interfaces/api/http-error';
import { getWireguardConfigById } from '../../../store/robots/robots-actions';
import useCustomLocalizationEffect from '../../../hooks/useCustomLocalizationEffect';
import MessageType from '@common/enums/robot/rsw/message-types';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CustomLocalizationType from '../../../models/enums/custom-localization-type';
import OrganizationRole from '../../../models/enums/organization-roles';
import MissionSyncButton from '../../missions/MissionSyncButton';
import { sendRobotWebsocketMessage } from 'store/middleware/robot-websocket-middleware';
import { useSharedSelector } from '@common/store/SharedProvider';
import { selectIsAdvancedModeEnabled } from '@common/store/app-configurations/app-configurations-selectors';
import DataSyncButton from '../interaction/DataSyncButton';
import { trackEvent } from 'services/analytics';
import { AnalyticsEvent, RobotAccessAction } from 'services/analytics-events';

const RobotAccessSettings: React.FC = () => {
  const { t } = useTranslation();
  const localize = useCustomLocalizationEffect();

  const dispatch = useAppDispatch();

  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const user = useAppSelector((state) => selectUser(state));
  const isAdvancedModeEnabled = useSharedSelector((state) => selectIsAdvancedModeEnabled(state));

  const [isBearerTokenVisible, setIsBearerTokenVisible] = useState(false);

  const robot = robotsData.currentRobot;

  useEffect(() => {
    if (
      robotsData.rest.getWireguardConfigStatus === HttpFetchStatus.Idle &&
      robotsData.currentRobot?.id != null
    )
      dispatch(getWireguardConfigById(robotsData.currentRobot.id));
  }, [
    dispatch,
    robotsData.currentRobot?.id,
    robotsData.rest.getWireguardConfigStatus,
    user.data.organization.role,
  ]);

  // Only which action was used is sent, never the id, token or configuration itself.
  const trackAccess = (accessAction: string) => {
    trackEvent(AnalyticsEvent.RobotAccessUsed, {
      access_action: accessAction,
      robot_id: robot?.id ?? null,
      robot_serial: robot?.serialNumber ?? null,
    });
  };

  const copyIdToClipboardClickHandler = () => {
    trackAccess(RobotAccessAction.CopyRobotId);
    if (robot?.id != null) {
      navigator.clipboard.writeText(robot.id);
    }
  };

  const copyAuthTokenToClipboardClickHandler = () => {
    trackAccess(RobotAccessAction.CopyAuthToken);
    if (robot?.authToken != null) navigator.clipboard.writeText(robot.authToken);
  };

  const refreshClickHandler = () => {
    trackAccess(RobotAccessAction.RefreshWireguardConfig);
    if (robot?.id != null) {
      dispatch(getWireguardConfigById(robot.id));
    }
  };

  const serviceConnectionClickHandler = (actionType: ActionType) => {
    trackAccess(actionType);
    dispatch(
      sendRobotWebsocketMessage({
        type: MessageType.Actions,
        message: { action: actionType },
      }),
    );
  };

  const downloadWireguardConfigClickHandler = () => {
    trackAccess(RobotAccessAction.DownloadWireguardConfig);
    if (robotsData.currentRobotData?.wireguardConfig != null) {
      const element = document.createElement('a');
      const file = new Blob([robotsData.currentRobotData.wireguardConfig], { type: 'text/plain' });
      element.href = URL.createObjectURL(file);
      element.download = 'wg0.conf';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    }
  };

  const copyWireguardConfigClickHandler = () => {
    trackAccess(RobotAccessAction.CopyWireguardConfig);
    if (robotsData.currentRobotData?.wireguardConfig != null) {
      navigator.clipboard.writeText(robotsData.currentRobotData.wireguardConfig);
    }
  };

  const toggleBearerTokenVisibility = () => {
    setIsBearerTokenVisible((prevState) => !prevState);
  };

  return (
    <React.Fragment>
      <Stack spacing={2} width="100%" alignItems="center" p={0.3}>
        <CustomSplitColumnCard
          title={t('title-robot-connection-settings')}
          content={[
            user.data.organization.role === OrganizationRole.MANUFACTURER
              ? {
                  left: (
                    <Stack spacing={1} pr={2}>
                      <Stack direction="row" alignItems="center" spacing={1} py={1}>
                        <CloudTokenIcon />
                        <Typography>{t('info-tooltip-access-settings')}</Typography>
                      </Stack>
                      <CustomAlert severity="info" header={t('info-access-settings-description')} />
                    </Stack>
                  ),
                  right: (
                    <CustomLabelTextTable
                      cellPadding={1}
                      rows={[
                        {
                          label: t('info-label-robot-id'),
                          text: (
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography variant="body2">{robot?.id}</Typography>
                              <TooltipIconButton
                                icon={<CopyIcon />}
                                tooltip={t('info-tooltip-copy-to-clipboard')}
                                onClick={copyIdToClipboardClickHandler}
                              />
                            </Stack>
                          ),
                        },
                        {
                          label: t('info-label-robot-auth-token'),
                          text: (
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {isBearerTokenVisible
                                  ? robot?.authToken
                                  : new Array((robot?.authToken ?? '').length).join('*')}
                              </Typography>
                              <Stack direction="row" spacing={1}>
                                <TooltipIconButton
                                  icon={
                                    isBearerTokenVisible ? <HideInputIcon /> : <ShowInputIcon />
                                  }
                                  tooltip={t('info-tooltip-toggle-visibility')}
                                  onClick={toggleBearerTokenVisibility}
                                />
                                <TooltipIconButton
                                  icon={<CopyIcon />}
                                  tooltip={t('info-tooltip-copy-to-clipboard')}
                                  onClick={copyAuthTokenToClipboardClickHandler}
                                />
                              </Stack>
                            </Stack>
                          ),
                        },
                      ]}
                    />
                  ),
                }
              : null,
            user.data.hasWireguardAccess
              ? {
                  left: (
                    <Stack spacing={1} pr={2}>
                      <Stack direction="row" alignItems="center" spacing={1} py={1}>
                        <ServiceConnectionIcon />
                        <Typography>{t('title-remote-access')}</Typography>
                      </Stack>
                      {robotsData.rest.getWireguardConfigStatus !== HttpFetchStatus.Success && (
                        <CustomAlert
                          severity="info"
                          action={
                            robotsData.rest.getWireguardConfigStatus === HttpFetchStatus.Failed ? (
                              <TooltipIconButton
                                onClick={refreshClickHandler}
                                tooltip={t('button-refresh')}
                                icon={<RefreshIcon color="primary" />}
                              />
                            ) : undefined
                          }
                          header={
                            robotsData.rest.getWireguardConfigStatus === HttpFetchStatus.Idle ||
                            robotsData.rest.getWireguardConfigStatus === HttpFetchStatus.Loading
                              ? t('loading-wireguard-configs')
                              : t('error-loading-wireguard-configs')
                          }
                          icon={
                            robotsData.rest.getWireguardConfigStatus === HttpFetchStatus.Idle ||
                            robotsData.rest.getWireguardConfigStatus === HttpFetchStatus.Loading ? (
                              <CircularProgress size={24} />
                            ) : undefined
                          }
                          textMessages={
                            robotsData.rest.getWireguardConfigError != null &&
                            isHttpError(robotsData.rest.getWireguardConfigError)
                              ? [t(robotsData.rest.getWireguardConfigError.message)]
                              : undefined
                          }
                        />
                      )}
                      {robotsData.currentRobotData?.wireguardConfig != null && (
                        <CustomAlert header={t('service-connection-info')} />
                      )}
                    </Stack>
                  ),
                  right: (
                    <Stack>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">
                          {robotsData.currentRobotData?.wireguardConfig == null
                            ? t('info-label-wireguard-configuration-missing')
                            : t('info-label-wireguard-configuration')}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <TooltipIconButton
                            color="primary"
                            tooltip={t('button-download')}
                            disabled={robotsData.currentRobotData?.wireguardConfig == null}
                            icon={<DownloadIcon />}
                            onClick={downloadWireguardConfigClickHandler}
                          />
                          <TooltipIconButton
                            color="primary"
                            disabled={robotsData.currentRobotData?.wireguardConfig == null}
                            tooltip={t('info-tooltip-copy-to-clipboard')}
                            icon={<CopyIcon />}
                            onClick={copyWireguardConfigClickHandler}
                          />
                        </Stack>
                      </Stack>
                      {[ActionType.OpenServiceConnection, ActionType.CloseServiceConnection].map(
                        (actionType) => {
                          const action = robotsData.currentRobotData?.actionsList?.actionList.find(
                            (a) => a.action === actionType,
                          );
                          if (action == null) return null;
                          return (
                            <Tooltip
                              placement="right"
                              title={
                                !action.isEnabled
                                  ? localize(
                                      action.disableReason,
                                      CustomLocalizationType.ACTION_DISABLE_REASON,
                                    )
                                  : undefined
                              }
                              key={actionType}
                            >
                              <span>
                                <Button
                                  onClick={() => serviceConnectionClickHandler(actionType)}
                                  disabled={!action.isEnabled}
                                >
                                  {t('button-' + actionType)}
                                </Button>
                              </span>
                            </Tooltip>
                          );
                        },
                      )}
                    </Stack>
                  ),
                }
              : null,
            {
              left: (
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <SyncIcon />
                  <Typography>{t('title-sync-robot')}</Typography>
                </Stack>
              ),
              right: (
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    width="100%"
                    alignItems="center"
                    spacing={2}
                    justifyContent="space-between"
                  >
                    <Typography variant="body2">
                      {t('info-label-sync-open-tasks-from-robot')}
                    </Typography>
                    <MissionSyncButton />
                  </Stack>
                  {isAdvancedModeEnabled && (
                    <Stack
                      direction="row"
                      width="100%"
                      alignItems="center"
                      spacing={2}
                      justifyContent="space-between"
                    >
                      <Typography variant="body2">
                        {t('info-label-sync-all-data-from-robot')}
                      </Typography>
                      <DataSyncButton />
                    </Stack>
                  )}
                </Stack>
              ),
            },
          ]}
        />
        <CustomSplitColumnCard
          title={t('title-robot-authentication-settings')}
          content={[
            robot?.authentication != null
              ? {
                  left: (
                    <Stack spacing={1} pr={2}>
                      <Stack direction="row" alignItems="center" spacing={1} py={1}>
                        <PasswordIcon />
                        <Typography>{t('title-robot-password-settings')}</Typography>
                      </Stack>
                      <CustomAlert
                        icon={
                          robot?.authentication.syncDetails == null ||
                          robot?.authentication.syncDetails.syncStatus === SyncStatus.FAILED ? (
                            <SyncErrorIcon />
                          ) : robot?.authentication?.syncDetails.syncStatus ===
                            SyncStatus.SUCCEEDED ? (
                            <SyncIcon />
                          ) : (
                            <Box
                              sx={(theme) => ({
                                position: 'relative',
                                height: theme.spacing(3),
                                width: theme.spacing(3),
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'space-around',
                              })}
                            >
                              <SyncIcon color="disabled" fontSize="small" />
                              <CircularProgress size={24} sx={{ position: 'absolute', left: 2 }} />
                            </Box>
                          )
                        }
                        header={
                          robot?.authentication.syncDetails == null
                            ? t('info-settings-never-synced')
                            : robot?.authentication.syncDetails.syncStatus === SyncStatus.FAILED
                              ? t('info-failed-to-sync')
                              : robot?.authentication.syncDetails.syncStatus ===
                                  SyncStatus.SUCCEEDED
                                ? t('info-successfully-synced')
                                : t('info-syncing-in-progress')
                        }
                        textMessages={
                          robot?.authentication.syncDetails?.lastSyncTime != null
                            ? [
                                `${t('label-last-synced')}: ${dateToStringFormat(
                                  robot?.authentication.syncDetails.lastSyncTime,
                                )}`,
                              ]
                            : undefined
                        }
                        severity={
                          robot?.authentication.syncDetails == null ||
                          robot?.authentication.syncDetails.syncStatus === SyncStatus.FAILED
                            ? robot?.authentication.syncDetails == null
                              ? 'warning'
                              : 'error'
                            : 'info'
                        }
                      />
                    </Stack>
                  ),
                  right: (
                    <AuthenticationSettings
                      robotId={robot.id}
                      authenticationSettings={robot.authentication}
                    />
                  ),
                }
              : null,
          ]}
        />
      </Stack>
    </React.Fragment>
  );
};

export default RobotAccessSettings;
